/**
 * BST Product Specifications — image slider with dot pagination.
 */
const BST_SPECS_IMAGES_SELECTORS = {
  track: '[data-specs-images-track]',
  slide: '.bst-product-specifications__image-slide',
  pagination: '[data-specs-images-dots]',
};

class BstProductSpecificationsImagesComponent extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.pagination = null;
    this.slides = [];
    this.dots = [];
    this.activeIndex = 0;

    this.onDotClick = this.onDotClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector(BST_SPECS_IMAGES_SELECTORS.track);
    this.pagination = this.querySelector(BST_SPECS_IMAGES_SELECTORS.pagination);

    if (!this.track || !this.pagination) return;

    this.slides = [...this.track.querySelectorAll(BST_SPECS_IMAGES_SELECTORS.slide)];
    if (this.slides.length < 2) {
      this.pagination.hidden = true;
      return;
    }

    this.buildDots();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.buildDots());
    });

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    document.addEventListener('shopify:section:load', this.onSectionLoad);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
    }
  }

  disconnectedCallback() {
    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
    }
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onSectionLoad(event) {
    if (!event.target.contains(this)) return;
    this.slides = [...this.track.querySelectorAll(BST_SPECS_IMAGES_SELECTORS.slide)];
    this.buildDots();
  }

  onResize() {
    if (this._resizeTimer) window.clearTimeout(this._resizeTimer);
    this._resizeTimer = window.setTimeout(() => this.buildDots(), 150);
  }

  onDotClick(event) {
    const index = parseInt(event.currentTarget.dataset.specsImagesDot, 10);
    if (Number.isNaN(index)) return;
    this.goToSlide(index);
  }

  onScroll() {
    if (this._scrollRaf) return;
    this._scrollRaf = window.requestAnimationFrame(() => {
      this._scrollRaf = null;
      this.updateActiveSlide();
    });
  }

  buildDots() {
    this.pagination.innerHTML = '';
    this.dots = [];

    if (this.slides.length < 2) {
      this.pagination.hidden = true;
      return;
    }

    this.pagination.hidden = false;

    this.slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bst-product-specifications__images-dot';
      dot.dataset.specsImagesDot = String(index);
      dot.setAttribute('aria-label', `Go to image ${index + 1}`);
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      dot.addEventListener('click', this.onDotClick);
      this.pagination.appendChild(dot);
      this.dots.push(dot);
    });

    this.updateActiveSlide(true);
  }

  getScrollOffsetForSlide(index) {
    const slide = this.slides[index];
    if (!slide) return 0;
    return Math.max(0, slide.offsetLeft - this.track.offsetLeft);
  }

  goToSlide(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    this.track.scrollTo({
      left: this.getScrollOffsetForSlide(clamped),
      behavior,
    });

    this.setActiveSlide(clamped);
  }

  updateActiveSlide(immediate = false) {
    if (!this.dots.length) return;

    const trackRect = this.track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    let closest = this.activeIndex;
    let closestDist = Infinity;

    this.slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.left + rect.width / 2;
      const dist = Math.abs(slideCenter - trackCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closest = index;
      }
    });

    this.setActiveSlide(closest, immediate);
  }

  setActiveSlide(index, immediate = false) {
    if (index === this.activeIndex && !immediate) return;

    this.activeIndex = index;

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('bst-product-specifications-images-component')) {
  customElements.define('bst-product-specifications-images-component', BstProductSpecificationsImagesComponent);
}
